import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, uploadFile, type Project, type ProjectKind, type UploadProgress } from "../api";

function formatBytes(value: string | number, zeroLabel = "—"): string {
  const bytes = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return zeroLabel;
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(digits)} ${units[unit]}`;
}

function formatEta(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const remain = total % 60;
  if (minutes < 60) return remain ? `${minutes}m ${remain}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function formatPublished(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function AdminPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [name, setName] = useState("");
  const [clientLabel, setClientLabel] = useState("");
  const [kind, setKind] = useState<ProjectKind>("mesh");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    const list = await api.listProjects();
    setProjects(list);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        if (!me.authenticated) {
          navigate("/admin/login");
          return;
        }
        setEmail(me.email ?? null);
        await refresh();
      } catch {
        navigate("/admin/login");
      }
    })();
  }, [navigate, refresh]);

  useEffect(() => {
    const needsPoll = projects.some(
      (p) => p.status === "uploading" || p.status === "processing",
    );
    if (!needsPoll) return;
    const id = window.setInterval(() => {
      void refresh();
    }, 2000);
    return () => window.clearInterval(id);
  }, [projects, refresh]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError("Choose a .3tz file");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".3tz")) {
      setError("Upload a .3tz archive (mesh from Metashape, or point cloud from scripts/tile-pointcloud.sh)");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    setUpload(null);
    try {
      const { project, upload: plan } = await api.createProject({
        name,
        clientLabel: clientLabel || undefined,
        byteSize: file.size,
        kind,
        format: "3tz",
      });
      await uploadFile(project.id, plan, file, setUpload);
      await api.completeUpload(project.id);
      setMessage(`Uploaded “${name}”. Processing started.`);
      setName("");
      setClientLabel("");
      setFile(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
      setUpload(null);
    }
  };

  const copyShare = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setMessage("Share link copied");
  };

  const deleteProject = async (project: Project) => {
    if (deletingIds.includes(project.id)) return;
    if (!confirm(`Delete “${project.name}”?`)) return;
    setDeletingIds((current) => [...current, project.id]);
    setError(null);
    try {
      await api.deleteProject(project.id);
      setProjects((current) => current.filter((item) => item.id !== project.id));
      setMessage(`Deleted “${project.name}”. Stored files are being removed in the background.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeletingIds((current) => current.filter((id) => id !== project.id));
    }
  };

  return (
    <div className="page admin-page">
      <header className="admin-header">
        <div>
          <img className="brand-logo" src="/dronevision-logo.png" alt="DroneVision" />
          <h1>Projects</h1>
          <p className="muted">Signed in as {email}</p>
        </div>
        <div className="row gap">
          <Link className="btn ghost" to="/">
            Home
          </Link>
          <button
            className="btn ghost"
            type="button"
            onClick={async () => {
              await api.logout();
              navigate("/admin/login");
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <form className="card upload-card" onSubmit={onCreate}>
        <h2>New project</h2>
        <p className="muted">
          Upload a pre-tiled .3tz archive. Mesh from Metashape, or point cloud from{" "}
          <code>scripts/tile-pointcloud.sh</code> on Docker Desktop. Raw LAS/LAZ/E57 are not uploaded here.
        </p>
        <div className="grid-2">
          <label>
            Project name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Client label (optional)
            <input value={clientLabel} onChange={(e) => setClientLabel(e.target.value)} />
          </label>
        </div>
        <fieldset className="kind-picker">
          <legend>Project type</legend>
          <label className="kind-option">
            <input
              type="radio"
              name="kind"
              checked={kind === "mesh"}
              onChange={() => setKind("mesh")}
            />
            Mesh
          </label>
          <label className="kind-option">
            <input
              type="radio"
              name="kind"
              checked={kind === "pointcloud"}
              onChange={() => setKind("pointcloud")}
            />
            Point cloud
          </label>
        </fieldset>
        <label>
          .3tz file
          <input
            type="file"
            accept=".3tz,application/zip"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            required
          />
        </label>
        {busy && !upload && <p className="upload-stats">Preparing upload…</p>}
        {upload && (
          <div className="upload-live">
            <div className="progress" aria-hidden="true">
              <div className="progress-bar" style={{ width: `${upload.percent}%` }} />
            </div>
            <p className="upload-stats">
              Uploading {upload.percent}% · {formatBytes(upload.loaded, "0 B")} of{" "}
              {formatBytes(upload.total, "0 B")}
              {upload.bytesPerSecond > 0 && <> · {formatBytes(upload.bytesPerSecond)}/s</>}
              {upload.percent >= 100
                ? " · Finishing…"
                : upload.etaSeconds != null
                  ? ` · ${formatEta(upload.etaSeconds)} left`
                  : " · Starting…"}
              {upload.parts != null && upload.part != null && upload.part > 0 && (
                <>
                  {" "}
                  · part {upload.part} of {upload.parts}
                </>
              )}
            </p>
          </div>
        )}
        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-ok">{message}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Working…" : "Upload & process"}
        </button>
      </form>

      <div className="card">
        <h2>All projects</h2>
        {projects.length === 0 ? (
          <p className="muted">No projects yet.</p>
        ) : (
          <div className="project-list">
            {projects.map((p) => (
              <article key={p.id} className={`project-row${deletingIds.includes(p.id) ? " deleting" : ""}`}>
                <div>
                  <strong>{p.name}</strong>
                  {p.clientLabel && <span className="muted"> · {p.clientLabel}</span>}
                  <div className="meta">
                    <span className={`badge ${p.status}`}>{p.status}</span>
                    <span className="badge kind">{p.kind === "pointcloud" ? "Point cloud" : "Mesh"}</span>
                    <span>Published {formatPublished(p.createdAt)}</span>
                    <span>{formatBytes(p.byteSize)}</span>
                    {p.status === "processing" && (
                      <span>
                        Processing {p.progress}%
                        {p.filesTotal > 0 &&
                          ` · ${p.filesWritten.toLocaleString()} of ${p.filesTotal.toLocaleString()} files`}
                      </span>
                    )}
                    {deletingIds.includes(p.id) && <span>Deleting…</span>}
                    {p.errorMessage && <span className="form-error inline">{p.errorMessage}</span>}
                  </div>
                </div>
                <div className="row gap wrap">
                  {p.status === "ready" && p.shareEnabled && (
                    <>
                      <a className="btn ghost" href={`/s/${p.shareToken}`} target="_blank" rel="noreferrer">
                        Open
                      </a>
                      <button className="btn ghost" type="button" onClick={() => copyShare(p.shareUrl)}>
                        Copy link
                      </button>
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={async () => {
                          await api.revokeShare(p.id);
                          await refresh();
                        }}
                      >
                        Revoke
                      </button>
                    </>
                  )}
                  {p.status === "ready" && !p.shareEnabled && (
                    <button
                      className="btn ghost"
                      type="button"
                      onClick={async () => {
                        await api.enableShare(p.id);
                        await refresh();
                      }}
                    >
                      New link
                    </button>
                  )}
                  <button
                    className="btn danger"
                    type="button"
                    disabled={deletingIds.includes(p.id)}
                    onClick={() => void deleteProject(p)}
                  >
                    {deletingIds.includes(p.id) ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
